/**
 * Google Analytics 4 (GA4) Utility Functions
 * Provides enhanced tracking capabilities for Lexa.run
 */

// Google Analytics Tracking ID
const GA_TRACKING_ID = 'G-8H0YPB039B';

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track page views
 * @param {string} url - The page URL
 * @param {string} title - The page title
 */
export const trackPageView = (url, title) => {
  if (isGtagAvailable()) {
    window.gtag('config', GA_TRACKING_ID, {
      page_location: url,
      page_title: title,
    });
  }
};

/**
 * Track custom events
 * @param {string} eventName - Name of the event
 * @param {string} category - Event category
 * @param {string} label - Event label (optional)
 * @param {number} value - Event value (optional)
 */
export const trackEvent = (eventName, category, label = null, value = null) => {
  if (isGtagAvailable()) {
    const eventData = {
      event_category: category,
    };
    
    if (label) eventData.event_label = label;
    if (value) eventData.value = value;
    
    window.gtag('event', eventName, eventData);
  }
};

/**
 * Track user registration
 * @param {string} method - Registration method (email, google, etc.)
 */
export const trackRegistration = (method = 'email') => {
  trackEvent('sign_up', 'engagement', 'method', method);
};

/**
 * Track user login
 * @param {string} method - Login method (email, google, etc.)
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', 'engagement', 'method', method);
};

/**
 * Track Flow Test creation
 * @param {string} testType - Type of test (ai_generated, manual)
 * @param {string} testName - Name of the test
 */
export const trackFlowTestCreation = (testType, testName) => {
  trackEvent('flow_test_created', 'flow_testing', testType, testName);
};

/**
 * Track Flow Test execution
 * @param {string} testId - ID of the test
 * @param {string} status - Execution status (success, failed, error)
 */
export const trackFlowTestExecution = (testId, status) => {
  trackEvent('flow_test_executed', 'flow_testing', status, testId);
};

/**
 * Track API Test creation
 * @param {string} testType - Type of API test
 * @param {string} endpoint - API endpoint being tested
 */
export const trackAPITestCreation = (testType, endpoint) => {
  trackEvent('api_test_created', 'api_testing', testType, endpoint);
};

/**
 * Track API Test execution
 * @param {string} testId - ID of the test
 * @param {string} status - Execution status (passed, failed)
 */
export const trackAPITestExecution = (testId, status) => {
  trackEvent('api_test_executed', 'api_testing', status, testId);
};

/**
 * Track subscription events
 * @param {string} action - Action taken (subscribe, upgrade, downgrade, cancel)
 * @param {string} plan - Plan name (free, starter, pro, business)
 */
export const trackSubscription = (action, plan) => {
  trackEvent('subscription', 'billing', action, plan);
};

/**
 * Track feature usage
 * @param {string} feature - Feature name
 * @param {string} action - Action taken
 */
export const trackFeatureUsage = (feature, action) => {
  trackEvent('feature_usage', 'engagement', feature, action);
};

/**
 * Set user properties
 * @param {string} userId - User ID
 * @param {object} properties - User properties
 */
export const setUserProperties = (userId, properties = {}) => {
  if (isGtagAvailable()) {
    window.gtag('config', GA_TRACKING_ID, {
      user_id: userId,
      custom_map: properties
    });
  }
};

/**
 * Track conversion events (for ads and goals)
 * @param {string} conversionName - Name of the conversion
 * @param {number} value - Conversion value (optional)
 */
export const trackConversion = (conversionName, value = null) => {
  if (isGtagAvailable()) {
    const eventData = {
      send_to: GA_TRACKING_ID
    };
    
    if (value) eventData.value = value;
    
    window.gtag('event', 'conversion', {
      ...eventData,
      event_category: 'conversion',
      event_label: conversionName
    });
  }
};

export default {
  trackPageView,
  trackEvent,
  trackRegistration,
  trackLogin,
  trackFlowTestCreation,
  trackFlowTestExecution,
  trackAPITestCreation,
  trackAPITestExecution,
  trackSubscription,
  trackFeatureUsage,
  setUserProperties,
  trackConversion
};